/**
 * hostScope.js — single source of truth for "which verqo portal are we on?"
 *
 * Verqo's URL architecture (final Feb 2026 — see docs/url_architecture.md):
 *
 *   verqo.com / www.verqo.com    → MARKETING       (anon allowed, /login here too)
 *   buyers.verqo.com             → BUYERS_PORTAL   (role=buyer required)
 *   sellers.verqo.com            → SELLER_PORTAL   (role=seller required)
 *   ops.verqo.com                → PLATFORM_OPS    (role=admin required, IP-allowlisted via CF Access)
 *   <slug>.verqo.com             → STOREFRONT      (closed network — seller's branded buyer-facing storefront)
 *   shop.<custom>.com            → STOREFRONT      (vanity domain via Cloudflare for SaaS)
 *   localhost / *.preview.…      → DEV_PREVIEW     (single-host dev — all routes available)
 *
 * Why this lives in one place:
 *   • Login.jsx needs it to decide where to redirect after auth
 *   • App.js needs it to gate which routes render on which host
 *   • api.js needs it to decide whether to use same-origin or REACT_APP_BACKEND_URL
 *
 * Reserved subdomains (must match backend RESERVED_TENANT_SLUGS):
 *   www, accounts, buyers, admin, ops, api, app, dev, staging, cname, …
 *   — these never resolve to a tenant.
 */

const RESERVED_SUBDOMAINS = new Set([
  "www", "accounts", "buyers", "sellers", "admin", "ops", "api", "app",
  "dev", "staging", "prod", "test", "cname", "shop",
  "mail", "email", "status", "docs", "blog", "help", "support",
  "cdn", "static", "assets", "media", "files", "secure", "login",
  "signup", "register", "auth", "oauth", "sso", "webhook", "webhooks",
]);

export const HOST_KIND = Object.freeze({
  MARKETING:     "MARKETING",
  BUYERS_PORTAL: "BUYERS_PORTAL",
  SELLER_PORTAL: "SELLER_PORTAL",
  PLATFORM_OPS:  "PLATFORM_OPS",
  STOREFRONT:    "STOREFRONT",     // <slug>.verqo.com OR shop.<custom>.com
  DEV_PREVIEW:   "DEV_PREVIEW",    // single-host dev / preview / localhost
});

/** Classify the current hostname. Used by App.js host-aware routing. */
export function classifyHost(hostname = window.location.host) {
  const h = (hostname || "").toLowerCase().split(":")[0];

  // Single-host dev / preview environments — every route is allowed.
  if (
    h === "localhost" ||
    h.endsWith(".localhost") ||
    h.endsWith(".preview.emergentagent.com") ||
    h.endsWith(".emergent.host")
  ) {
    return HOST_KIND.DEV_PREVIEW;
  }

  if (h === "verqo.com" || h === "www.verqo.com") return HOST_KIND.MARKETING;
  if (h === "buyers.verqo.com")  return HOST_KIND.BUYERS_PORTAL;
  if (h === "sellers.verqo.com") return HOST_KIND.SELLER_PORTAL;
  if (h === "admin.verqo.com")   return HOST_KIND.SELLER_PORTAL;  // legacy alias — see Worker for 301 redirect
  if (h === "ops.verqo.com")     return HOST_KIND.PLATFORM_OPS;

  // <slug>.verqo.com → storefront (excluding reserved subdomains above).
  if (h.endsWith(".verqo.com")) {
    const slug = h.slice(0, -".verqo.com".length);
    if (slug && !RESERVED_SUBDOMAINS.has(slug)) return HOST_KIND.STOREFRONT;
    // Reserved fall-through — treat as marketing (defensive, shouldn't hit).
    return HOST_KIND.MARKETING;
  }

  // Any other host (seller's vanity domain shop.brand.com, etc.) → storefront.
  // Backend `/api/public/resolve-host` will tell us which tenant.
  return HOST_KIND.STOREFRONT;
}

/**
 * Where should a user with this role land RIGHT AFTER signing in?
 *
 * In production we use absolute URLs to JUMP to the role-specific subdomain
 * (buyers.verqo.com or sellers.verqo.com). In dev/preview we stay on the same
 * host because there are no subdomains — just relative paths.
 *
 * Override with `?next=/some/path` on the login URL — useful for invite
 * links that want to land on a specific page after auth.
 */
export function postLoginUrl(role, { nextPath = null, hostname = window.location.host } = {}) {
  const scope = classifyHost(hostname);
  const safeNext = sanitizeNextPath(nextPath);

  // Dev / preview — single host, return relative paths so React Router handles it.
  if (scope === HOST_KIND.DEV_PREVIEW) {
    if (safeNext) return safeNext;
    if (role === "admin")  return "/admin";
    if (role === "seller") return "/seller";
    if (role === "buyer")  return "/buyer";
    // Partner roles keep their existing portals.
    const partnerMap = {
      banking_partner:   "/banking",
      lender_partner:    "/lender",
      insurance_partner: "/insurance",
      carrier_partner:   "/carrier",
    };
    return partnerMap[role] || "/";
  }

  // Production — absolute URL to the right subdomain.
  const base = {
    buyer:             "https://buyers.verqo.com",
    seller:            "https://sellers.verqo.com",
    admin:             "https://ops.verqo.com",
    banking_partner:   "https://sellers.verqo.com",      // partners share the seller host for now
    lender_partner:    "https://sellers.verqo.com",
    insurance_partner: "https://sellers.verqo.com",
    carrier_partner:   "https://sellers.verqo.com",
  }[role] || "https://verqo.com";

  const path = safeNext || ({
    buyer:             "/buyer",
    seller:            "/seller",
    admin:             "/admin",
    banking_partner:   "/banking",
    lender_partner:    "/lender",
    insurance_partner: "/insurance",
    carrier_partner:   "/carrier",
  }[role] || "/");

  return `${base}${path}`;
}

/** Strict allowlist for `?next=` to prevent open-redirect attacks. */
function sanitizeNextPath(next) {
  if (!next || typeof next !== "string") return null;
  // Only relative paths starting with a single slash + no protocol/host.
  if (!next.startsWith("/")) return null;
  if (next.startsWith("//")) return null;        // protocol-relative
  if (next.includes(":")) return null;           // protocol smuggling
  if (next.length > 200) return null;
  return next;
}

/**
 * API base URL. We prefer same-origin (`""` → axios uses current host's
 * `/api/...`) in production because:
 *   • No CORS preflight cost
 *   • Cookies just work (no SameSite=None gymnastics)
 *   • Each subdomain's frontend talks to its own backend pod
 *
 * In dev/preview we fall back to REACT_APP_BACKEND_URL so localhost:3000
 * can hit the remote preview backend.
 */
export function getApiBase() {
  const scope = classifyHost();
  if (scope === HOST_KIND.DEV_PREVIEW) {
    return process.env.REACT_APP_BACKEND_URL || "";
  }
  // Same-origin — empty base means axios calls `/api/...` on the current host.
  return "";
}


/**
 * Where should a signed-out user land?
 *
 * In production, every portal subdomain (buyers / admin / ops) is a closed
 * area — once the session is gone there's nothing for the user to see, so we
 * push them back to the canonical marketing login at verqo.com/login. This
 * also avoids the awkward case where the user sees the marketing landing
 * page served from buyers.verqo.com (which technically works through the
 * Worker but reads weird).
 *
 * In dev/preview (single host) we stay on the same host with `/login`.
 */
export function postLogoutUrl({ hostname = window.location.host } = {}) {
  const scope = classifyHost(hostname);
  if (scope === HOST_KIND.DEV_PREVIEW || scope === HOST_KIND.MARKETING) {
    return "/login"; // same host
  }
  // Portal subdomains, storefronts → always bounce to canonical login.
  return "https://verqo.com/login";
}
